import React from "react";

// Mirrors ContentBlocks.tsx in beleefVendorApp, which is what the vendor actually reads.
const isHidden = (block) =>
  block?.items?.length === 1 && block.items[0]?.metadata?.hidden;

// The chat reads *bold*, **bold** and _italic_, so admin has to read them the same way.
const markup = (text, keyPrefix) => {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g);
  return parts.map((part, index) => {
    const key = `${keyPrefix}-${index}`;
    if (/^\*\*[^*]+\*\*$/.test(part))
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    if (/^\*[^*]+\*$/.test(part))
      return <strong key={key}>{part.slice(1, -1)}</strong>;
    if (/^_[^_]+_$/.test(part)) return <em key={key}>{part.slice(1, -1)}</em>;
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
};

// The chat bolds a line that ends in a colon and turns "- " and "* " into bullets.
const textLines = (value) =>
  String(value || "")
    .split("\n")
    .map((line) => ({
      bold: /^.+:$/.test(line),
      text: line.replace(/^[ \t]*[-*][ \t]+/, "• "),
    }));

const Block = ({ block }) => {
  if (block?.type === "text") {
    return (
      <p className="whitespace-pre-wrap text-sm">
        {textLines(block.value).map((line, index) => (
          <React.Fragment key={index}>
            {line.bold ? (
              <strong>{line.text}</strong>
            ) : (
              markup(line.text, index)
            )}
            {"\n"}
          </React.Fragment>
        ))}
      </p>
    );
  }

  if (block?.type === "actions") {
    return (
      <div className="flex flex-wrap gap-2">
        {(block.items || [])
          .filter((item) => !item?.metadata?.hidden)
          .map((item, index) => (
            <span
              key={`${item.label}-${index}`}
              className="rounded-full border border-mediumgray px-3 py-1 text-xs text-darkergray"
            >
              {item.label}
            </span>
          ))}
      </div>
    );
  }

  if (block?.type === "list") {
    return (
      <ul className="list-disc space-y-1 pl-5 text-sm">
        {(block.items || []).map((item, index) => (
          <li key={`${item.label}-${index}`}>{item.label}</li>
        ))}
      </ul>
    );
  }

  // input and propertyList are interactive; say they are there rather than drop them.
  return (
    <p className="text-xs italic text-darkgray">
      The vendor sees an interactive {block?.type} here, which cannot be shown from admin.
    </p>
  );
};

// The chat hoists ONE image block above everything and drops any others.
const MessageBlocks = ({ blocks, bare = false }) => {
  if (!blocks?.length) return null;

  const imageBlock = blocks.find((block) => block?.type === "image");
  const rest = blocks.filter((block) => block?.type !== "image" && !isHidden(block));
  const images = imageBlock?.items || [];

  return (
    <div
      className={
        bare ? "space-y-2" : "space-y-2 rounded-md border border-mediumgray p-3"
      }
    >
      {images.length ? (
        <div className="relative">
          <img
            src={images[0].url}
            alt={images[0].name || "attached"}
            className="h-48 w-full rounded-md object-cover"
          />
          {images.length > 1 ? (
            <span className="absolute right-2 top-2 rounded-md bg-black/60 px-2 py-1 text-xs text-white">
              +{images.length - 1}
            </span>
          ) : null}
        </div>
      ) : null}

      {rest.map((block, index) => (
        <Block key={`${block?.type}-${index}`} block={block} />
      ))}
    </div>
  );
};

export default MessageBlocks;
